import type * as THREE from 'three'
import type { ImportedModelInstancedUnitSource } from '../../../../types'
import type { ImportedModelInstancedMeshUnit } from './ImportedModelInstancedMeshUnit'

/**
 * InstancedMesh/source Mesh 到 Unit 的 Viewer 级运行时注册表。
 *
 * 注册表只提供当前 Viewer 内 O(1) 命中解析，不保存 CAD 拓扑或业务历史，也不会进入 SDK
 * 公共根出口和持久化协议。
 */
export class ImportedModelInstancedMeshRegistry implements ImportedModelInstancedUnitSource {
  /** 派生资源订阅，只通知 Unit 成员变化，不受移动/旋转或临时透明影响。 */
  private readonly unitListeners = new Set<(unit: ImportedModelInstancedMeshUnit, present: boolean) => void>()

  /** @param listener 成员通知。 @returns 解除监听；订阅失败也必须移除已登记回调。 */
  public observeUnits(listener: (unit: ImportedModelInstancedMeshUnit, present: boolean) => void): () => void {
    this.assertAvailable()
    this.unitListeners.add(listener)
    try {
      this.unitByObject.forEach(unit => listener(unit, true))
    } catch (error) {
      this.unitListeners.delete(listener)
      throw error
    }
    return () => { this.unitListeners.delete(listener) }
  }
  /** InstancedMesh 对象引用到当前 Unit 的索引。 */
  private readonly unitByObject = new Map<THREE.Object3D, ImportedModelInstancedMeshUnit>()

  /** 被正式实例投影接管的 source Mesh 到当前 Unit 的反向索引。 */
  private readonly unitBySourceMesh = new Map<THREE.Mesh, ImportedModelInstancedMeshUnit>()

  /** 已脱离主场景树的 source Mesh UUID 到对象引用的稳定索引。 */
  private readonly sourceMeshByUuid = new Map<string, THREE.Mesh>()

  /** 当前模型显示模式是否要求绘制默认拓扑边线。 */
  private topologyDisplayEdgesEnabled = false

  /** 当前模型显示模式要求的默认拓扑边线颜色。 */
  private topologyDisplayEdgeColor: THREE.ColorRepresentation = 0x000000

  /** 注册表是否已经结束 Viewer 生命周期。 */
  private disposed = false

  /** Unit 注册/解除注册共享的单调语义修订。 */
  private semanticRevision = 0
  /** 包括装配和独立阵列 Unit 的命令外观范围；不进入身份版本。 */
  private readonly temporaryAppearances = new Map<string, PuppyCAD.ImportedModelTemporaryAppearanceRequest>()

  /** @returns 当前 Viewer 实例 Unit 候选资格的单调修订。 */
  public getSemanticRevision(): number {
    return this.semanticRevision
  }

  /**
   * 注册一个已经完成离屏校验的 Unit。
   *
   * @param unit 待发布实例组。
   */
  public register(unit: ImportedModelInstancedMeshUnit): void {
    this.assertAvailable()
    if (this.unitByObject.has(unit.object)) {
      throw new Error(`InstancedMeshUnit 重复注册：${unit.object.uuid}`)
    }
    const sourceMeshes = unit.listSourceMeshes()
    const duplicatedSourceMesh = sourceMeshes.find(sourceMesh => this.unitBySourceMesh.has(sourceMesh))
    if (duplicatedSourceMesh) {
      throw new Error(`实例投影 source Mesh 重复注册：${duplicatedSourceMesh.uuid}`)
    }
    this.unitByObject.set(unit.object, unit)
    sourceMeshes.forEach((sourceMesh) => {
      this.unitBySourceMesh.set(sourceMesh, unit)
      this.sourceMeshByUuid.set(sourceMesh.uuid, sourceMesh)
    })
    this.semanticRevision += 1
    /** Unit 重建必须继承当前 View 派生状态，不能依赖调用方在重建后再次扫描场景。 */
    unit.setTopologyDisplayEdgesEnabled(
      this.topologyDisplayEdgesEnabled,
      this.topologyDisplayEdgeColor,
    )
    this.temporaryAppearances.forEach((request, owner) => {
      unit.setTemporaryOpacity(owner, unit.listSourceMeshesForObject(request.root), request.opacity)
    })
    this.unitListeners.forEach(listener => listener(unit, true))
  }

  /**
   * 按 owner 更新临时外观，所有已登记领域共用此入口，不调用装配 Coordinator.rebuild。
   * @param owner 命令外观身份。
   * @param root 当前逻辑组件；null 释放该 owner。
   * @param opacity 有限的 0..1 透明度。
   * @returns 是否改变显示状态。
   */
  public setTemporaryOpacity(owner: string, root: THREE.Object3D | null, opacity: number): boolean {
    if (this.disposed) return false
    if (!owner.trim() || !Number.isFinite(opacity) || opacity < 0 || opacity > 1) {
      throw new Error('实例临时外观需要有效 owner 与 0..1 透明度')
    }
    const previous = this.temporaryAppearances.get(owner)
    let changed = false
    try {
      this.unitByObject.forEach(unit => {
        changed = unit.setTemporaryOpacity(owner, root ? unit.listSourceMeshesForObject(root) : [], opacity) || changed
      })
      if (root) this.temporaryAppearances.set(owner, { root, opacity })
      else this.temporaryAppearances.delete(owner)
      return changed
    } catch (error) {
      // 跨 Unit 发布失败时恢复本 owner 的旧范围，不把部分发布当作成功。
      this.unitByObject.forEach(unit => {
        unit.setTemporaryOpacity(owner, previous ? unit.listSourceMeshesForObject(previous.root) : [], previous?.opacity ?? 1)
      })
      throw error
    }
  }

  /**
   * 解除一个 Unit 的射线命中映射。
   *
   * @param unit 待解除实例组。
   */
  public unregister(unit: ImportedModelInstancedMeshUnit): void {
    if (this.disposed) {
      return
    }
    let changed = false
    const current = this.unitByObject.get(unit.object)
    if (current === unit) {
      this.temporaryAppearances.forEach((_request, owner) => unit.setTemporaryOpacity(owner, [], 1))
      this.unitByObject.delete(unit.object)
      changed = true
    }
    unit.listSourceMeshes().forEach((sourceMesh) => {
      if (this.unitBySourceMesh.get(sourceMesh) === unit) {
        this.unitBySourceMesh.delete(sourceMesh)
        this.sourceMeshByUuid.delete(sourceMesh.uuid)
        changed = true
      }
    })
    if (changed) {
      this.semanticRevision += 1
      this.unitListeners.forEach(listener => listener(unit, false))
    }
  }

  /**
   * 按场景对象解析当前 Unit。
   *
   * @param object 射线命中的场景对象。
   * @returns 已注册 Unit；普通对象返回 `null`。
   */
  public resolve(object: THREE.Object3D | null | undefined): ImportedModelInstancedMeshUnit | null {
    if (this.disposed || !object) {
      return null
    }
    return this.unitByObject.get(object) ?? null
  }

  /**
   * 判断对象是否是当前 Viewer 正式发布的导入实例组。
   *
   * @param object 待判断对象。
   * @returns 是否存在活动 Unit。
   */
  public has(object: THREE.Object3D | null | undefined): boolean {
    return this.resolve(object) !== null
  }

  /**
   * 判断 source Mesh 是否正由当前 Viewer 的正式实例投影接管。
   *
   * @param sourceMesh 导入模型原始显示 Mesh。
   * @returns source Mesh 存在活动 Unit 映射时返回 true。
   */
  public hasSourceMesh(sourceMesh: THREE.Mesh | null | undefined): boolean {
    if (this.disposed || !sourceMesh) {
      return false
    }
    return this.unitBySourceMesh.has(sourceMesh)
  }

  /**
   * 按 source Mesh 解析当前正式接管其显示职责的实例 Unit。
   *
   * @param sourceMesh 导入模型原始显示 Mesh。
   * @returns 当前活动 Unit；普通或业务隐藏 Mesh 返回 null。
   */
  public resolveSourceMesh(
    sourceMesh: THREE.Mesh | null | undefined,
  ): ImportedModelInstancedMeshUnit | null {
    if (this.disposed || !sourceMesh) {
      return null
    }
    return this.unitBySourceMesh.get(sourceMesh) ?? null
  }

  /**
   * 按 UUID 解析已脱离主场景树、但仍由实例投影接管的 source Mesh。
   *
   * @param objectId 业务 Object3D UUID。
   * @returns 活动 source Mesh；未命中时返回 null。
   */
  public resolveDetachedSourceMeshById(objectId: string): THREE.Mesh | null {
    if (this.disposed) {
      return null
    }
    return this.sourceMeshByUuid.get(objectId) ?? null
  }

  /**
   * 解析 source Mesh 对应的权威 occurrence 变换根。
   *
   * @param sourceMesh 实例命中回映射得到的业务 Mesh。
   * @returns occurrence 变换根；普通 Mesh 返回 null。
   */
  public resolveOccurrenceRoot(sourceMesh: THREE.Mesh): THREE.Object3D | null {
    if (this.disposed) {
      return null
    }
    return this.unitBySourceMesh.get(sourceMesh)?.resolveOccurrenceRoot(sourceMesh) ?? null
  }

  /**
   * 收集指定业务对象范围内全部活动 source Mesh。
   *
   * @param rootObject occurrence 根、装配祖先或 source Mesh 自身。
   * @returns 按 Unit 发布顺序去重后的业务 Mesh。
   */
  public listSourceMeshesForObject(rootObject: THREE.Object3D): readonly THREE.Mesh[] {
    if (this.disposed) {
      return Object.freeze([])
    }
    const sourceMeshes = new Set<THREE.Mesh>()
    this.unitByObject.forEach((unit) => {
      unit.listSourceMeshesForObject(rootObject).forEach((sourceMesh) => {
        sourceMeshes.add(sourceMesh)
      })
    })
    return Object.freeze([...sourceMeshes])
  }

  /**
   * 刷新活动 source Mesh 的有效世界矩阵。
   *
   * @param sourceMesh 待进行包围盒、测量或碰撞查询的业务 Mesh。
   * @returns 当前 Mesh 由活动 Unit 接管且刷新成功时返回 true。
   */
  public prepareSourceMeshForSpatialRead(sourceMesh: THREE.Mesh): boolean {
    if (this.disposed) {
      return false
    }
    return this.unitBySourceMesh.get(sourceMesh)
      ?.prepareSourceMeshForSpatialRead(sourceMesh) ?? false
  }

  /**
   * 将当前模型显示模式同步到全部活动实例 Unit 的默认拓扑边线。
   *
   * @param enabled 是否显示默认拓扑边线。
   * @param color 当前主题要求的边线颜色。
   * @returns 成功接管实例边线的 Unit 数量。
   */
  public syncTopologyDisplayEdges(
    enabled: boolean,
    color: THREE.ColorRepresentation,
  ): number {
    if (this.disposed) {
      return 0
    }
    /** 先保存权威显示状态，后续注册或重建的 Unit 将在发布时自动继承。 */
    this.topologyDisplayEdgesEnabled = enabled
    this.topologyDisplayEdgeColor = color
    let synchronizedUnitCount = 0
    this.unitByObject.forEach((unit) => {
      if (unit.setTopologyDisplayEdgesEnabled(enabled, color)) {
        synchronizedUnitCount += 1
      }
    })
    return synchronizedUnitCount
  }

  /** 结束注册表生命周期并断开全部运行时引用。 */
  public dispose(): void {
    if (this.disposed) {
      return
    }
    this.unitByObject.forEach(unit => {
      this.temporaryAppearances.forEach((_request, owner) => unit.setTemporaryOpacity(owner, [], 1))
    })
    this.disposed = true
    this.unitByObject.forEach(unit => this.unitListeners.forEach(listener => listener(unit, false)))
    this.unitListeners.clear()
    if (this.unitByObject.size > 0 || this.unitBySourceMesh.size > 0) {
      this.semanticRevision += 1
    }
    this.unitByObject.clear()
    this.unitBySourceMesh.clear()
    this.sourceMeshByUuid.clear()
    this.temporaryAppearances.clear()
  }

  /** 注册表销毁后禁止继续发布 Unit。 */
  private assertAvailable(): void {
    if (this.disposed) {
      throw new Error('导入模型 InstancedMesh 注册表已释放')
    }
  }
}
